import {
  CampaignType,
  ObjectRelationshipTriggerType,
  ObjectTriggerType,
  TRIGGER_CATEGORIES_NAMES_FOR_TRACKING,
  TriggerCategory,
} from 'cio/models/campaign';
import { Data, TransformsList } from 'cio/utils/analytics/transforms';
import actionInfo from 'cio/utils/action-info';
import { CAMPAIGN_FILTERS } from 'cio/utils/campaign-newsletter-filters';
import { isEmpty } from '@ember/utils';
import { capitalize } from '@ember/string';
import { generateFilteredTimeFrameData } from 'cio/utils/analytics/transforms/metrics';
import ActionModel, { ActionType } from 'cio/models/action';
import { MEASURE_DEFINITIONS, MeasureType } from 'cio/utils/metrics/measure';
import { JourneyMetricsMeasure } from 'cio/utils/journey-metrics';
import { Measure } from 'cio/utils/metrics/common';
import { Grouping } from 'cio/utils/metrics/resolution';
import { SortDirection } from 'cio/utils/sort';
import { COLOR_NAMES } from 'cio/utils/colors';
import { getModelEntryNameFromId } from 'cio/utils/analytics/transforms/utils';
import ObjectTypeModel from 'cio/models/object-type';
import TagModel from 'cio/models/tag';
import { CAMPAIGN_SORT_OPTIONS } from 'cio/utils/campaigns/sort-options';

const CAMPAIGN_TYPE_LABELS = {
  [CampaignType.Transactional]: 'Event',
  [CampaignType.Behavioral]: 'Segment',
  [CampaignType.Date]: 'Date',
  [CampaignType.APITriggered]: 'Triggered Broadcast',
  [CampaignType.Form]: 'Form',
  [CampaignType.Webhook]: 'Webhook',
  [CampaignType.None]: 'None',
};

const GROUPING_TYPE_LABELS = {
  [Grouping.Hourly]: 'By Hour',
  [Grouping.Daily]: 'By Day',
  [Grouping.Weekly]: 'By Week',
  [Grouping.Monthly]: 'By Month',
};

export enum WorkflowCopyModalActions {
  WorkspaceSelected = 'workspace_selected',
  CampaignSelected = 'campaign_selected',
  PlacementChosen = 'placement_chosen',
  PlacementUndo = 'undo_placement',
  ModalClosed = 'modal_closed',
  SaveAndCloseClicked = 'save_and_close_clicked',
  SaveAndGoToWorkflowClicked = 'save_and_go_to_workflow_clicked',
  WorkflowItemsSelected = 'workflow_items_selected',
  SelectEntireWorkflowClicked = 'select_entire_workflow_clicked',
  CopySelectedClicked = 'copy_selected_clicked',
}

export enum WorkflowCopyModalAnalyticsEvents {
  CopyFrom = 'workflow_existing_item_copy_from_modal_action_completed',
  CopyTo = 'workflow_existing_item_copy_to_modal_action_completed',
}

const MODAL_ACTION_TYPE_LABELS = {
  [WorkflowCopyModalActions.WorkspaceSelected]: 'Workspace Selected',
  [WorkflowCopyModalActions.CampaignSelected]: 'Campaign Selected',
  [WorkflowCopyModalActions.PlacementChosen]: 'Placement Chosen',
  [WorkflowCopyModalActions.PlacementUndo]: 'Undo Placement',
  [WorkflowCopyModalActions.ModalClosed]: 'Modal Closed',
  [WorkflowCopyModalActions.SaveAndCloseClicked]: 'Save and Close Clicked',
  [WorkflowCopyModalActions.SaveAndGoToWorkflowClicked]: 'Save and Go To Workflow Clicked',
  [WorkflowCopyModalActions.WorkflowItemsSelected]: 'Workflow Items Selected',
  [WorkflowCopyModalActions.SelectEntireWorkflowClicked]: 'Select Entire Workflow Clicked',
  [WorkflowCopyModalActions.CopySelectedClicked]: 'Copy Selected Clicked',
};

const CHART_UNIT_LABELS: Record<string, string> = {
  count: 'Count',
  percentage: 'Percentage',
};

const CHART_TYPE_LABELS: Record<string, string> = {
  individual: 'Individual',
  aggregate: 'Aggregate',
};

export enum EditType {
  TimeRange = 'time_range',
  Grouping = 'grouping',
  ChartUnit = 'chart_unit',
  ChartType = 'chart_type',
  Channels = 'channels',
  Channel = 'channel',
  Tab = 'tab',
  SortBy = 'sort_by',
  SortDirection = 'sort_direction',
  SelectedMetrics = 'selected_metrics',
  IndividualMetric = 'individual_metric',
}

const EDIT_TYPE_LABELS: Record<EditType, string> = {
  [EditType.TimeRange]: 'Timeframe changed',
  [EditType.Grouping]: 'Group by changed',
  [EditType.ChartUnit]: 'Chart unit changed',
  [EditType.ChartType]: 'Chart type changed',
  [EditType.Channels]: 'Channels changed',
  [EditType.Channel]: 'Channel changed',
  [EditType.Tab]: 'Selected tab changed',
  [EditType.SortBy]: 'Sort by changed',
  [EditType.SortDirection]: 'Sort order changed',
  [EditType.SelectedMetrics]: 'Selected metrics changed',
  [EditType.IndividualMetric]: 'Individual metric changed',
};

export enum ClickSource {
  Tab = 'tab',
  OverviewConversionChart = 'overview_conversion_chart',
  OverviewABTestCard = 'overview_ab_test_card',
  OverviewLeaderboardCard = 'overview_leaderboard_card',
  OverviewDetailsCard = 'overview_details_card',
  MetricsMessagesTable = 'metrics_messages_table',
  WorkflowItem = 'workflow_item',
}

const CLICK_SOURCE_LABELS: Record<ClickSource, string> = {
  [ClickSource.Tab]: 'Tab',
  [ClickSource.OverviewConversionChart]: 'Overview Conversion Chart',
  [ClickSource.OverviewABTestCard]: 'Overview A/B Test Card',
  [ClickSource.OverviewLeaderboardCard]: 'Overview Leaderboard Card',
  [ClickSource.OverviewDetailsCard]: 'Overview Details Card',
  [ClickSource.MetricsMessagesTable]: 'Metrics Messages Table',
  [ClickSource.WorkflowItem]: 'Workflow Item',
};

const OVERVIEW_CHART_HOVER_TYPE_LABELS: Record<
  MeasureType,
  Partial<Record<Measure | JourneyMetricsMeasure, string>>
> = {
  [MeasureType.DeliveryMetrics]: {
    opened: 'Opened Messages',
    clicked: 'Clicked Messages',
  },
  [MeasureType.JourneyMetrics]: {
    messaged: 'Journeys Messaged',
    converted: 'Journeys Converted',
  },
};

const SORT_DIRECTION_LABELS: Record<SortDirection, string> = {
  asc: 'Least',
  desc: 'Most',
};

const OBJECT_CAMPAIGN_TRIGGER_TYPE_LABEL: {
  object: Record<ObjectTriggerType, string>;
  relationship: Record<ObjectRelationshipTriggerType, string>;
} = {
  object: {
    [ObjectTriggerType.Created]: 'Object Created',
    [ObjectTriggerType.Updated]: 'Object Updated',
    [ObjectTriggerType.Deleted]: 'Object Deleted',
  },
  relationship: {
    [ObjectRelationshipTriggerType.Created]: 'Relationship Created',
    [ObjectRelationshipTriggerType.Updated]: 'Relationship Changed',
    [ObjectRelationshipTriggerType.Deleted]: 'Relationship Deleted',
  },
};

function generateBaseCampaignData(data: Data) {
  let model = data.model!;
  let type = (CAMPAIGN_TYPE_LABELS as any)[model.type];

  if ([CampaignType.Object, CampaignType.ObjectRelationship].includes(model.type)) {
    if (model.relationshipCampaign != null) {
      type =
        OBJECT_CAMPAIGN_TRIGGER_TYPE_LABEL.relationship[
          model.relationshipCampaign.type as ObjectRelationshipTriggerType
        ];
    } else if (model.objectCampaign != null) {
      type = OBJECT_CAMPAIGN_TRIGGER_TYPE_LABEL.object[model.objectCampaign.type as ObjectTriggerType];
    }
  }

  let eventData: Record<string, any> = {
    'Campaign ID': model.id.replace('copy_', ''),
    'Campaign Type': type,
    'Campaign State': capitalize(model.state),
  };

  if (data.source) {
    eventData.Source = CLICK_SOURCE_LABELS[data.source as ClickSource];
  }

  return eventData;
}

function baseExistingItem(data: Data) {
  return {
    ...generateBaseCampaignData(data),
    'Campaign Action IDs': actionIds(data.actions),
    'Workflow Item Types': data.actions.map((a: ActionModel) => actionType(a)),
    'Selected Workflow Item Count': data.actions.length,
  };
}

function languageCount(data: Data) {
  return data.model?.getLanguageCountByMessagingAction(data.action.id) ?? 0;
}

function actionType(action: any) {
  // For split tests, we want to say "AB Test".
  if (action.isSplitTest || action.type === 'split_randomized_action') {
    return 'AB Test';
  }

  let info = actionInfo(action!.type);
  return info ? info.analyticsName : 'Unknown';
}

function actionIds(actions: ActionModel[]) {
  return actions.map((a) => a.id.replace('copy_', ''));
}

function generateExistingItemOperationData(data: Data) {
  return {
    ...generateBaseCampaignData(data),
    ...baseExistingItem(data),
    'Tray Tab': capitalize(data.trayTab),
    'Workflow Bulk Action Initiation Type': data.initiationType,
  };
}

type CampaignSetupTab = 'trigger' | 'workflow' | 'goal_and_exit';

const Transforms: TransformsList = {
  base(data) {
    return generateBaseCampaignData(data);
  },

  reviewRouteView(data) {
    let errors = [];

    if (!data.model!.areRecipientsReady) {
      errors.push('Trigger');
    }

    if (!data.model!.hasMessages) {
      errors.push('Workflow');
    }

    if (!data.model!.validConversion) {
      errors.push('Goal And Exit');
    }

    return {
      ...generateBaseCampaignData(data),
      'Review Errors Present': errors,
    };
  },

  zoom(data) {
    return {
      ...generateBaseCampaignData(data),
      'Zoom Level': Math.floor(data.scale),
      'Zoom to Fit': data.zoomToFit,
      'Tray Tab': capitalize(data.trayTab),
      'Zoom Control Type': data.controlType,
    };
  },

  newWorkflowItem(data) {
    let eventData: Record<string, any> = {
      ...generateBaseCampaignData(data),
      'Workflow Item Type': actionType(data.action),
    };

    if (data.addAggressivePositivityModeInfo) {
      eventData['Positivity Mode On'] = data.aggressivePositivityModeEnabled ?? false;
    }

    return eventData;
  },

  existingItem(data) {
    return {
      ...generateBaseCampaignData(data),
      'Campaign Action ID': data.action.id,
      'Workflow Item Type': actionType(data.action),
      'Tray Tab': capitalize(data.trayTab),
      'Language Count': languageCount(data),
    };
  },

  existingItemOperation(data) {
    return {
      ...generateExistingItemOperationData(data),
    };
  },

  existingItemCopyToStarted(data) {
    return {
      ...generateBaseCampaignData(data),
      ...baseExistingItem(data),
    };
  },

  existingItemCopyFromOrToCompleted(data) {
    return {
      ...generateBaseCampaignData(data),
      ...baseExistingItem(data),
      'Copied From Same Workspace': data.sameWorkspace,
    };
  },

  existingItemCopyModalActionCompleted(data) {
    return {
      ...generateBaseCampaignData(data),
      ...baseExistingItem(data),
      'Modal Action Type': MODAL_ACTION_TYPE_LABELS[data.modalActionType as WorkflowCopyModalActions],
    };
  },

  existingItemCopy(data) {
    if (data.multiple) {
      return {
        ...generateBaseCampaignData(data),
        'Workflow Item Type': 'Multiple',
        'Copied From Same Workspace': !data.differentWorkspace,
        'Tray Tab': capitalize(data.trayTab),
      };
    } else {
      return {
        ...generateBaseCampaignData(data),
        'Campaign Action ID': data.action.id,
        'Workflow Item Type': actionType(data.action),
        'Copied From Same Workspace': !data.differentWorkspace,
        'Tray Tab': capitalize(data.trayTab),
      };
    }
  },

  existingItemPreview(data: Data) {
    return {
      ...generateBaseCampaignData(data),
      'Workflow Item Type': actionType(data.action),
      'Language Count': languageCount(data),
    };
  },

  editDiscard(data: Data & { from: CampaignSetupTab | 'name_and_description' }) {
    let attrs: { [key: string]: string } = {
      ...generateBaseCampaignData(data),
      'Edit(s) Discarded On': {
        trigger: 'Trigger Tab',
        workflow: 'Workflow Tab',
        goal_and_exit: 'Goal & Exit Tab',
        name_and_description: 'Name or Description',
      }[data.from],
    };

    if (data.action) {
      attrs['Workflow Item Type'] = actionType(data.action);
    }

    return attrs;
  },

  listView(data) {
    let { route } = data;
    if (!route) {
      return {};
    }

    let sort = CAMPAIGN_SORT_OPTIONS.find((item) => item.value === route?.queryParams.sort);
    let type = CAMPAIGN_FILTERS.find((item) => item.value === route?.queryParams.type);
    let tags = getModelEntryNameFromId<TagModel>(route.queryParams.searchTagIds, data.tags);
    let objectTypes =
      data.model != null
        ? getModelEntryNameFromId<ObjectTypeModel>(route.queryParams.objectTypeIds, [
            ...data.model.hasMany('objectTypes').value(),
          ])
        : false;

    return {
      'Filtered By Campaign Search': !isEmpty(route.queryParams.searchString),
      'Filtered by Campaign Type': type ? type.analyticsName : 'All campaigns',
      'Sorted By': sort ? sort.label : 'Sent emails',
      'Sort Order': route.queryParams.asc ? 'Descending' : 'Ascending',
      ...(tags !== false && { 'Filtered By Campaign Tag(s)': tags }),
      ...(objectTypes !== false && { 'Filtered by Object Types': objectTypes }),
    };
  },

  triggerFiltered(
    data: Data & { selectedView: (typeof TRIGGER_CATEGORIES_NAMES_FOR_TRACKING)[TriggerCategory]; searchTerm: string },
  ) {
    return {
      'Selected View': data.selectedView,
      ...(data.searchTerm && { 'Search Term': data.searchTerm }),
    };
  },

  metricsChartHover(data) {
    let eventData: Record<string, any> = {
      ...generateBaseCampaignData(data),
      'Time of Hover': data.time,
      'Selected Metrics': data.selectedMetrics.map(
        (m: Measure | JourneyMetricsMeasure) => MEASURE_DEFINITIONS[data.measureType as MeasureType][m].label,
      ),
    };

    if (data.channel != null) {
      eventData.Channel = actionInfo(`${data.channel}_action` as ActionType).analyticsName;
    }

    if (data.messagesSelectedCount != null) {
      eventData['Messages Selected Count'] = data.messagesSelectedCount;
    }

    if (data.chartType != null) {
      eventData['Chart Type'] = CHART_TYPE_LABELS[data.chartType as 'individual' | 'aggregate'];
    }

    return eventData;
  },

  performanceDeliveryChartChanged(data) {
    return {
      ...generateBaseCampaignData(data),
      'Selected Metrics': data.selectedMetrics.map((m: Measure) => MEASURE_DEFINITIONS.delivery_metrics[m].label),
      'Messages Selected Count': data.messagesSelectedCount ?? 0,
      Channel: actionInfo(`${data.channel}_action` as ActionType).analyticsName,
      'Chart Type': CHART_TYPE_LABELS[data.chartType as 'individual' | 'aggregate'],
      'Edit Type': EDIT_TYPE_LABELS[data.editType as EditType],
    };
  },

  journeyMetricsChartChanged(data) {
    return {
      ...generateBaseCampaignData(data),
      'Selected Metrics': data.selectedMetrics.map(
        (m: JourneyMetricsMeasure) => MEASURE_DEFINITIONS.journey_metrics[m].label,
      ),
    };
  },

  followCampaignToggled(data) {
    return {
      ...generateBaseCampaignData(data),
      'Follow Value': data.follow,
    };
  },

  unfollowCampaignFeedback(data) {
    return {
      ...generateBaseCampaignData(data),
      Feedback: data.praise,
    };
  },

  trayTabChanged(data) {
    return {
      ...generateBaseCampaignData(data),
      'Tray Tab': capitalize(data.trayTab),
      ...generateFilteredTimeFrameData(data),
      'Selected Metrics': data.selectedMetrics.map(capitalize),
    };
  },

  trayMetricsTab(data) {
    return {
      ...generateBaseCampaignData(data),
      ...generateFilteredTimeFrameData(data),
      'Selected Metrics': data.selectedMetrics.map(capitalize),
    };
  },

  volumePerformanceMetricsFiltered(data) {
    return {
      ...generateBaseCampaignData(data),
      'Filtered Messages Included': data.messageFilter,
      'Filtered By': capitalize(data.filteredBy),
      ...generateFilteredTimeFrameData(data),
      'Filtered By Chart View Mode': capitalize(data.graphMode),
      'Filtered By Chart Data Series': data.graphLegendState,
    };
  },

  stopOrArchiveStart(data) {
    return {
      ...generateBaseCampaignData(data),
      'Has Unsent Drafts': data.hasUnsentDrafts,
      'Has Journeys In Progress': data.hasJourneysInProgress,
    };
  },

  metricsTimeframeFilterChanged(data) {
    return {
      ...generateBaseCampaignData(data),
      ...generateFilteredTimeFrameData(data),
      'Group By Value': GROUPING_TYPE_LABELS[data.grouping as Grouping],
      'Chart Unit': CHART_UNIT_LABELS[data.chartUnit],
      'Edit Type': EDIT_TYPE_LABELS[data.editType as EditType],
    };
  },

  overviewTimeframeFilterChanged(data) {
    return {
      ...generateBaseCampaignData(data),
      ...generateFilteredTimeFrameData(data),
      'Group By Value': GROUPING_TYPE_LABELS[data.grouping as Grouping],
      Channels: data.channels.map((channel: string) => actionInfo(`${channel}_action` as ActionType).analyticsName),
      'Edit Type': EDIT_TYPE_LABELS[data.editType as EditType],
    };
  },

  overviewChartHover(data) {
    return {
      ...generateBaseCampaignData(data),
      'Time of Hover': data.time,
      'Chart Type':
        OVERVIEW_CHART_HOVER_TYPE_LABELS[data.measureType as MeasureType][
          data.measure as Measure | JourneyMetricsMeasure
        ],
    };
  },

  leaderboardCardDisplayChanged(data) {
    return {
      ...generateBaseCampaignData(data),
      'Selected Tab': capitalize(data.tab),
      'Sort Order': SORT_DIRECTION_LABELS[data.sortDirection as SortDirection],
      'Edit Type': EDIT_TYPE_LABELS[data.editType as EditType],
    };
  },

  abTestStarIconHovered(data) {
    return {
      ...generateBaseCampaignData(data),
      'Campaign Action ID': data.action.id,
    };
  },

  messageMetricsTableChanged(data) {
    return {
      ...generateBaseCampaignData(data),
      Channels: data.channels.map((channel: string) => actionInfo(`${channel}_action` as ActionType).analyticsName),
      'Selected Metrics': data.selectedMetrics.map((m: Measure) => MEASURE_DEFINITIONS.delivery_metrics[m].label),
      'Table Sorted By':
        data.sortBy === 'action_name' ? 'Action Name' : MEASURE_DEFINITIONS.delivery_metrics[data.sortBy].label,
      'Sort Order': SORT_DIRECTION_LABELS[data.sortDirection as SortDirection],
      'Edit Type': EDIT_TYPE_LABELS[data.editType as EditType],
    };
  },

  note(data) {
    return {
      ...generateBaseCampaignData(data),
      'Campaign Note ID': data.note.id,
    };
  },

  noteColorStarted(data) {
    return {
      ...generateBaseCampaignData(data),
      'Campaign Note ID': data.note.id,
      'Original Color': COLOR_NAMES[data.note.color] ?? data.note.color,
    };
  },

  noteColorCompleted(data) {
    return {
      ...generateBaseCampaignData(data),
      'Campaign Note ID': data.note.id,
      'Original Color': COLOR_NAMES[data.originalColor] ?? data.originalColor,
      'New Color': COLOR_NAMES[data.newColor] ?? data.newColor,
    };
  },

  webhookUrlBase(data) {
    return {
      ...generateBaseCampaignData(data),
      Source: data.source,
    };
  },

  webhookDataReceived(data) {
    return {
      ...generateBaseCampaignData(data),
      Source: data.source,
      'Webhook User Agent': data.logSource?.ua,
      'Webhook IP Address': data.logSource?.ipAddr,
    };
  },

  positivityModeChanged(data) {
    return {
      ...generateBaseCampaignData(data),
      'Toggled On': data.enabled,
    };
  },

  workflowImageExport(data) {
    return {
      ...generateBaseCampaignData(data),
      'Tray Tab': capitalize(data.trayTab),
    };
  },

  triggerTabViewed(data: Data) {
    return {
      ...generateBaseCampaignData(data),
      'Selected Trigger': data.selectedTrigger,
      'Trigger Tab Type': data.triggerTabType,
    };
  },

  backfillCompleted(data: Data) {
    return {
      'Match Selection': data.matchSelection,
    };
  },

  emptyStateSelectTemplate(data: Data) {
    return {
      'Template Name': data.title,
    };
  },
};

export default Transforms;
